import { Box } from "@/components/ui";
import { Chip } from "@/components/ui";
import { useTheme } from "@mui/material/styles";

/** Fixed width for all plan chips so they align. */
const PLAN_CHIP_WIDTH = 140;

const PLAN_LABELS = {
  trial: "Trial",
  professional: "Professional",
  ultra: "Premium",   // was Ultra → Premium with crown
  extreme: "Ultra",   // was Extreme → Ultra with lightning
  starter: "Trial",
  enterprise: "Ultra",
};

/**
 * Plan styles. Trial uses theme text.primary (no static colors).
 * ultra = Premium, extreme = Ultra (lightning).
 */
const PLAN_STYLES = {
  trial: { icon: null },
  professional: {
    border: "2px solid #1E88E5",
    color: "#1E88E5",
    icon: null,
  },
  ultra: {
    border: "2px solid #C62828",
    color: "#FF8A65",
    icon: "crown",
  },
  extreme: {
    border: "2px solid #AE98FE",
    color: "#AE98FE",
    icon: "lightning",
  },
};

function CrownIcon({ sx }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", color: "inherit", "& svg": { width: 16, height: 16 }, ...sx }} aria-hidden>
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 2c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1v-1h14v1z" />
      </svg>
    </Box>
  );
}

function LightningIcon({ sx }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", color: "inherit", "& svg": { width: 16, height: 16 }, ...sx }} aria-hidden>
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M13 2L3 14h6l-2 8 10-12h-6l2-8z" />
      </svg>
    </Box>
  );
}

/**
 * Plan name chip. Trial, Professional, Premium (crown), Ultra (lightning).
 * @param {string} plan - Plan key: "trial" | "professional" | "ultra" (Premium) | "extreme" (Ultra)
 * @param {boolean} [matchCardBackground] - If true, chip uses background.elevated (same as plan card BOX)
 */
export default function PlanChip({ plan, matchCardBackground, sx, ...chipProps }) {
  const theme = useTheme();
  const rawKey = (plan || "trial").toString().toLowerCase();
  const planKey =
    rawKey === "starter"
      ? "trial"
      : rawKey === "enterprise"
        ? "extreme"
        : rawKey;
  const label = PLAN_LABELS[planKey] || planKey.charAt(0).toUpperCase() + planKey.slice(1);
  const style = PLAN_STYLES[planKey] || PLAN_STYLES.trial;
  const isTrial = planKey === "trial";

  const iconNode =
    style.icon === "crown"
      ? <CrownIcon />
      : style.icon === "lightning"
        ? <LightningIcon />
        : null;

  const chipSx = {
    width: "100%",
    justifyContent: "center",
    px: 0.75,
    py: 0.75,
    fontSize: "0.9375rem",
    fontWeight: 600,
    border: isTrial ? "none" : style.border,
    bgcolor:
      matchCardBackground && theme.palette.background?.paper
        ? theme.palette.background.paper
        : "background.paper",
    color: isTrial ? "text.primary" : style.color,
    "& .MuiChip-label": { color: isTrial ? "text.primary" : style.color, fontWeight: 600 },
    "& .MuiChip-icon": { color: isTrial ? "text.primary" : style.color },
    ...sx,
  };

  const wrapperSx = {
    position: "relative",
    display: "inline-flex",
    width: PLAN_CHIP_WIDTH,
    minWidth: PLAN_CHIP_WIDTH,
    ...(isTrial && {
      border: "2px solid",
      borderColor: "text.primary",
      borderRadius: "9999px",
      boxSizing: "border-box",
    }),
  };

  return (
    <Box sx={wrapperSx}>
      <Chip icon={iconNode} label={label} sx={chipSx} {...chipProps} />
    </Box>
  );
}
